import React, {forwardRef, Fragment, useState} from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {useTranslation} from "react-i18next";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import DatasetAttachments from "../dataset-attachments";
import SanitizedHTML from "../sanitized-html";

const sectionTitleStyle = {
  fontWeight: "bold",
  marginBottom: "8px"
};

const sectionContentStyle = {
  "& p:first-child": {
    marginTop: "0px"
  },
  "& p:last-child": {
    marginBottom: "0px"
  },
  "& .MuiChip-root": {
    margin: "0 8px 0 0"
  }
};

export const INFO_BUTTON_VIEW_MODE_ICON_BUTTON = "INFO_BUTTON_VIEW_MODE_ICON_BUTTON";
export const INFO_BUTTON_VIEW_MODE_BUTTON = "INFO_BUTTON_VIEW_MODE_BUTTON";
export const INFO_BUTTON_VIEW_MODE_HIDDEN = "INFO_BUTTON_VIEW_MODE_HIDDEN";

const CatalogInfoButton = forwardRef(
  ({title, source, description, note, attachments, viewMode = INFO_BUTTON_VIEW_MODE_ICON_BUTTON, label}, ref) => {
    const {t} = useTranslation();
    const [isVisible, setVisibility] = useState(false);

    const onOpen = () => {
      setVisibility(true);
    };

    const onClose = ev => {
      ev.stopPropagation();
      setVisibility(false);
    };

    if (!source && !description && !note && !attachments) {
      return <span />;
    }

    return (
      <Fragment>
        {viewMode === INFO_BUTTON_VIEW_MODE_ICON_BUTTON ? (
          <Tooltip title={t("components.catalogInfoButton.action.title")}>
            <IconButton
              id="catalog-info-btn"
              size="small"
              onClick={ev => {
                ev.stopPropagation();
                onOpen();
              }}
              aria-label={t("components.catalogInfoButton.action.ariaLabel")}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        ) : viewMode === INFO_BUTTON_VIEW_MODE_BUTTON ? (
          <Tooltip title={t("components.catalogInfoButton.action.title")}>
            <Button
              id="catalog-info-btn"
              onClick={ev => {
                ev.stopPropagation();
                onOpen();
              }}
              aria-label={t("components.catalogInfoButton.action.ariaLabel")}
            >
              {label || t("components.catalogInfoButton.action.label")}
            </Button>
          </Tooltip>
        ) : viewMode === INFO_BUTTON_VIEW_MODE_HIDDEN ? (
          <Button ref={ref} id="catalog-info-btn" onClick={onOpen} sx={{display: "none"}} />
        ) : null}

        <CustomDialog open={isVisible} onClose={onClose}>
          <CustomDialogTitle onClose={onClose}>
            {t("components.catalogInfoButton.dialog.title", {datasetTitle: title})}
          </CustomDialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              {source && (
                <Grid container>
                  <Grid size={12} sx={sectionTitleStyle}>
                    {t("components.catalogInfoButton.dialog.content.source")}:
                  </Grid>
                  <Grid size={12} sx={sectionContentStyle}>
                    {source}
                  </Grid>
                </Grid>
              )}
              {description && (
                <Grid container>
                  <Grid size={12} sx={sectionTitleStyle}>
                    {t("components.catalogInfoButton.dialog.content.description")}:
                  </Grid>
                  <Grid size={12} sx={sectionContentStyle}>
                    <SanitizedHTML html={description} allowTarget />
                  </Grid>
                </Grid>
              )}
              {note && (
                <Grid container>
                  <Grid size={12} sx={sectionTitleStyle}>
                    {t("components.catalogInfoButton.dialog.content.notes")}:
                  </Grid>
                  <Grid size={12} sx={sectionContentStyle}>
                    <SanitizedHTML html={note} allowTarget />
                  </Grid>
                </Grid>
              )}
              {attachments && (
                <Grid container>
                  <Grid size={12} sx={sectionTitleStyle}>
                    {t("components.catalogInfoButton.dialog.content.attachments")}:
                  </Grid>
                  <Grid size={12} sx={sectionContentStyle}>
                    <DatasetAttachments title={title} attachedDataFiles={attachments} />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{t("commons.confirm.close")}</Button>
          </DialogActions>
        </CustomDialog>
      </Fragment>
    );
  }
);

export default CatalogInfoButton;
